<template>
  <div class="bitts-form-label">
    <div class="flex">
      <label
        :id="labelId"
        :for="forId"
        :class="{
          'bitts-form-label__text--disabled': disabled,
        }"
        class="bitts-form-label__text"
      >
        {{ labelText }}
      </label>
      <BittsTooltip
        v-if="formLabel?.helpText"
        :button-text="formLabel?.ctaText"
        :include-cta="!!formLabel?.ctaText"
        :learn-more-link="formLabel?.ctaLink"
        :placement="formLabel?.placement"
      >
        <div class="leading-4">
          <FontAwesomeIcon
            :icon="['fak', 'information-circle']"
            :style="{ height: '12px', width: '12px', color: 'currentColor' }"
            class="text-neutral-text-button"
          />
        </div>
        <template #title>
          {{ formLabel?.helpText }}
        </template>
      </BittsTooltip>
    </div>
    <slot name="secondaryText">
      <span
        v-if="formLabel?.secondaryText"
        class="text-neutral-text-weak text-right text-sm"
        :class="{
          'bitts-form-label__secondary-text--disabled': disabled,
        }"
      >
        {{ formLabel.secondaryText }}
      </span>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';

import BittsTooltip from '../BittsTooltip/BittsTooltip.vue';

import { FormLabelType } from './types';

/* Force every label to declare how it is associated with its control:
 * `for` for a single control (plus an optional `id` when the control also
 * references the label through aria-labelledby), `id` (+ aria-labelledby on the
 * group) for a group of controls, or `decorative` when the label is not tied to
 * a form control (e.g. a section heading). `decorative` is a type-only opt-out
 * with no runtime use. */
/* eslint-disable vue/no-unused-properties */
type AssociationProps =
  | { for: string; id?: string; decorative?: never }
  | { id: string; for?: never; decorative?: never }
  | { decorative: true; for?: never; id?: never };
/* eslint-enable vue/no-unused-properties */

export type Props = {
  label: FormLabelType | string;
  disabled?: boolean;
} & AssociationProps;

const props = defineProps<Props>();

const forId = computed(() => props.for);
const labelId = computed(() => props.id);
const formLabel = computed(() => props.label as FormLabelType);
const labelText = computed(() =>
  typeof props.label === 'string' ? props.label : props.label?.title,
);
</script>

<style lang="pcss" scoped>
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-form-label {
  @apply flex justify-between items-center mb-6;
  &.bitts-form-label--disabled {
    @apply text-neutral-text-placeholder opacity-100!;
  }
}
.bitts-form-label__text {
  @apply text-neutral-text-strong font-bold text-sm mr-4;
  &.bitts-form-label__text--disabled {
    @apply text-neutral-text-placeholder opacity-100!;
  }
}
</style>
